import { action } from '@ember/object';
import type Owner from '@ember/owner';
import { service } from '@ember/service';
import Component from '@glimmer/component';
import { cached, tracked } from '@glimmer/tracking';

import type { Service } from 'webapp-types';
import { localizeFormatOptionLabels } from 'webapp/utils/number';

import didUpdate from 'webapp/modifiers/did-update';
import type { ActionOption } from 'webapp/components/form/select-with-action';
import FormSelectWithActionComponent from 'webapp/components/form/select-with-action';
import MetricsPopupCustomFormatComponent from 'webapp/components/metrics/popup-custom-format';

import type DsCompare from 'webapp/models-custom/ds-compare';

interface Args {
	model: DsCompare;
	onChange: (format: string) => void;
	isDisabled?: boolean;
}

export default class FormSelectCompareFormatComponent extends Component<Args> {
	@service('account') declare accountService: Service.Account;
	@service('popup') declare popupService: Service.Popup;
	@service('model-manager/metrics') declare mmMetricsService: Service.ModelManager.Metrics;

	@tracked model!: DsCompare;
	@tracked options: LabelValuePairString[] = [];

	constructor(owner: Owner, args: Args) {
		super(owner, args);

		this.updatePropertiesFromModel();
	}

	@cached
	get displayOptions(): LabelValuePairString[] {
		return localizeFormatOptionLabels(this.options, this.accountService.isEuropeanNumberFormat);
	}

	get dropdownActions(): ActionOption[] {
		const text = 'New custom format';
		return [
			{
				id: null,
				text,
				isSpecial: true,
				onClick: () => {
					const popup = this.popupService.createModal(MetricsPopupCustomFormatComponent, {
						popupClass: 'popup-custom-format',
					});
					popup.open({
						onSave: (format: string) => {
							this.addCustomFormat(format);
						},
					});
				},
				position: 'bottom',
				html: `<a class="format-add">${text}</a>`,
			},
		];
	}

	@action
	onModelChange() {
		const oldModel = this.model,
			newModel = this.args.model;
		if (newModel.id !== oldModel.id) {
			this.updatePropertiesFromModel();
		}
	}

	@action
	onFunctionChange() {
		this.updateOptions();
	}

	private addCustomFormat(format: string) {
		const newChangeFormat = {
			value: format,
			label: format,
		};
		this.options = [...this.options, newChangeFormat];
		this.args.onChange(format);
	}

	private updatePropertiesFromModel() {
		const model = this.args.model;
		this.model = model;
		this.updateOptions();
	}

	private updateOptions() {
		const format = this.model.format;

		const formatOptions = this.mmMetricsService.getChangeFormatValuesForChangeFunction(
			this.model.function,
		);
		if (format && !formatOptions.find((o) => o.value === format)) {
			formatOptions.push({
				label: format,
				value: format,
			});
		}
		this.options = formatOptions;
	}
	<template>
		<div
			class="compare--format"
			{{didUpdate this.onModelChange @model}}
			{{didUpdate this.onFunctionChange @model.function}}
		>
			<FormSelectWithActionComponent
				name="compareFormat"
				@selected={{this.model.format}}
				@options={{this.displayOptions}}
				@isNumeric={{false}}
				@isDisabled={{@isDisabled}}
				@placeholder="Change format"
				@tooltipOnSelectionFromProperty=""
				@isRequired={{true}}
				@actionOptions={{this.dropdownActions}}
				@isDropdownWider={{true}}
				@extraClass="dropdown-format"
				@onChange={{@onChange}}
			/>
		</div>
	</template>
}
